import axios from "axios";

import type { LivenessSession } from "../../components/face-liveness/FaceLivenessCheck";

/**
 * Instância axios EXCLUSIVA do quiosque (ADR 0031), pelo mesmo motivo do
 * `employeeApi`: o `api` do staff injeta o token do staff no interceptor. O
 * token do aparelho fica no `localStorage` do próprio aparelho e só vai para
 * `/dp/kiosk/*`.
 */
const TOKEN_KEY = "kioskToken";

export const readKioskToken = (): string | null => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const saveKioskToken = (token: string | null) => {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* sem armazenamento: o aparelho pede o código de novo na próxima abertura */
  }
};

const kioskApi = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 20000,
});

kioskApi.interceptors.request.use((config) => {
  const token = readKioskToken();
  if (token) (config.headers as any).Authorization = `Bearer ${token}`;
  return config;
});

/** Erro do quiosque: `offline` sem resposta do servidor; `unauthorized` = aparelho a reativar. */
export class KioskError extends Error {
  constructor(
    message: string,
    public readonly code: string | null,
    public readonly status: number | null,
  ) {
    super(message);
  }
  get offline() {
    return this.status === null;
  }
  get unauthorized() {
    return this.status === 401;
  }
}

const call = async <T,>(fallback: string, fn: () => Promise<{ data: T }>): Promise<T> => {
  try {
    return (await fn()).data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (!error.response) throw new KioskError("Sem conexão.", null, null);
      const data = error.response.data as any;
      const message = typeof data?.message === "string" ? data.message : Array.isArray(data?.message) ? data.message.join(" | ") : fallback;
      throw new KioskError(message, data?.code ?? null, error.response.status);
    }
    throw new KioskError(fallback, null, null);
  }
};

export type KioskStatus = {
  serverNow: string;
  device: { id: string; name: string };
  store: { id: string; name: string };
  timekeepingEnabled: boolean;
  recognitionAvailable: boolean;
};

export type KioskOutcome = "MATCH" | "NO_MATCH" | "NOT_LIVE";

export type KioskIdentification = {
  attemptId: string;
  outcome: KioskOutcome;
  employee: { id: string; displayName: string } | null;
  /** Sorteada para a prova de vida (ADR 0043): só marca pelo rosto depois de aprovada. */
  livenessRequired: boolean;
  cpfAllowed: boolean;
};

export type KioskCandidate = { id: string; displayName: string };

export type KioskReceipt = {
  id: string;
  nsr: number;
  time: string;
  day: string;
  label: string;
  employeeName: string;
  identification: "FACE" | "CPF" | null;
  /** Início do código hash (SHA-256) da marcação: o oficial da Portaria 671 quando há. */
  hashShort: string;
};

export const activateKioskApi = (code: string) =>
  call<{ token: string; device: { id: string; name: string; store: { id: string; name: string } } }>("Erro ao ativar o aparelho.", () =>
    kioskApi.post("/dp/kiosk/activate", { code }),
  );

export const kioskStatusApi = () => call<KioskStatus>("Erro ao consultar o aparelho.", () => kioskApi.get("/dp/kiosk/status"));

export const startKioskLivenessApi = () => call<LivenessSession>("Erro ao abrir a verificação.", () => kioskApi.post("/dp/kiosk/liveness-sessions"));

/** Quadro da câmera (ADR 0043): a identificação normal, sem toque. */
export const identifyFrameApi = (frame: Blob) => {
  const form = new FormData();
  form.append("photo", frame, "quadro.jpg");
  return call<KioskIdentification>("Erro ao identificar.", () => kioskApi.post("/dp/kiosk/attempts", form, { headers: { "Content-Type": "multipart/form-data" } }));
};

/** Prova de vida da identificação sorteada. */
export const confirmLivenessApi = (attemptId: string, sessionId: string) =>
  call<KioskIdentification>("Erro na verificação do rosto.", () => kioskApi.post(`/dp/kiosk/attempts/${attemptId}/liveness`, { sessionId }));

/** Caminho de reserva (ADR 0031): prova de vida primeiro. */
export const identifyApi = (sessionId: string) =>
  call<KioskIdentification>("Erro ao identificar.", () => kioskApi.post("/dp/kiosk/identify", { sessionId }));

export const cpfApi = (attemptId: string, cpfPrefix: string) =>
  call<{ candidates: KioskCandidate[]; triesLeft: number }>("Erro ao buscar o CPF.", () => kioskApi.post(`/dp/kiosk/attempts/${attemptId}/cpf`, { cpfPrefix }));

export const kioskPunchApi = (body: { attemptId: string; employeeId: string; clientRequestId: string; clientTimestamp: string }) =>
  call<KioskReceipt>("Erro ao marcar o ponto.", () => kioskApi.post("/dp/kiosk/punches", body));
